(() => {
  if (customElements.get('product-buy-get-gift')) return;

  class ProductBuyGetGift extends HTMLElement {
    constructor() {
      super();

      this.isAddingGift = false;
      this.mainProductPending = false;
      this.pendingTimer = null;

      this.handleProductSubmit = this.handleProductSubmit.bind(this);
      this.handleProductAdded = this.handleProductAdded.bind(this);
      this.handleProductError = this.handleProductError.bind(this);
    }

    connectedCallback() {
      this.bindProductForm();

      if (!this.productForm) {
        requestAnimationFrame(() => this.bindProductForm());
      }

      document.addEventListener(
        'ajaxProduct:added',
        this.handleProductAdded
      );

      document.addEventListener(
        'ajaxProduct:error',
        this.handleProductError
      );
    }

    disconnectedCallback() {
      this.productForm?.removeEventListener(
        'submit',
        this.handleProductSubmit,
        true
      );

      document.removeEventListener(
        'ajaxProduct:added',
        this.handleProductAdded
      );

      document.removeEventListener(
        'ajaxProduct:error',
        this.handleProductError
      );

      clearTimeout(this.pendingTimer);
    }

    bindProductForm() {
      const formId = this.dataset.productFormId;
      const productForm = document.getElementById(formId);

      if (!productForm || productForm === this.productForm) return;

      this.productForm?.removeEventListener(
        'submit',
        this.handleProductSubmit,
        true
      );

      this.productForm = productForm;

      this.productForm.addEventListener(
        'submit',
        this.handleProductSubmit,
        true
      );
    }

    get triggerProductId() {
      return Number(this.dataset.triggerProductId);
    }

    get giftProductId() {
      return Number(this.dataset.giftProductId);
    }

    get giftVariantId() {
      return Number(this.dataset.giftVariantId);
    }

    get cartAddUrl() {
      return this.dataset.cartAddUrl || theme.routes.cart_add_url;
    }

    get cartUrl() {
      const cartUrl =
        this.dataset.cartUrl ||
        theme.routes.cart_url ||
        '/cart';

      if (cartUrl.endsWith('.js')) {
        return cartUrl;
      }

      return `${cartUrl.replace(/\/$/, '')}.js`;
    }

    get messageElement() {
      return this.querySelector('[data-gift-message]');
    }

  handleProductSubmit(event) {
  if (window.theme?.settings?.cartType === 'page') {
    event.preventDefault();
    event.stopImmediatePropagation();

    this.addMainProductForPageCart(
      event.currentTarget,
      event.submitter
    );

    return;
  }

  this.mainProductPending = true;

  clearTimeout(this.pendingTimer);

  this.pendingTimer = setTimeout(() => {
    this.mainProductPending = false;
  }, 15000);
}

async addMainProductForPageCart(form, submitButton) {
  this.hideMessage();

  submitButton?.setAttribute('aria-disabled', 'true');
  submitButton?.setAttribute('aria-busy', 'true');

  try {
    const formData = new FormData(form);

    const response = await fetch(this.cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    const addedProduct = await response.json();

    if (!response.ok || addedProduct.status) {
      throw new Error(
        addedProduct.description ||
        addedProduct.message ||
        'The product could not be added to the cart.'
      );
    }

    await this.addGiftIfNeeded();

    window.location.assign(
      this.cartUrl.replace(/\.js$/, '')
    );
  } catch (error) {
    console.error(
      'Unable to complete the promotional offer:',
      error
    );

    this.showMessage(
      error.message ||
      'The promotional offer could not be added.'
    );

    submitButton?.removeAttribute('aria-disabled');
    submitButton?.removeAttribute('aria-busy');
  }
}

    handleProductAdded(event) {
      const addedProduct = event.detail?.product;
      const confirmedByResponse =
        this.containsTriggerProduct(addedProduct);

      if (!this.mainProductPending && !confirmedByResponse) return;

      this.resetPendingState();

      if (this.isAddingGift) return;

      this.addGiftIfNeeded();
    }

    handleProductError() {
      this.resetPendingState();
    }

    resetPendingState() {
      this.mainProductPending = false;
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }

    containsTriggerProduct(addedProduct) {
      if (!addedProduct) return false;

      if (
        Number(addedProduct.product_id) ===
        this.triggerProductId
      ) {
        return true;
      }

      if (Array.isArray(addedProduct.items)) {
        return addedProduct.items.some((item) => {
          return (
            Number(item.product_id) ===
            this.triggerProductId
          );
        });
      }

      return false;
    }

    async addGiftIfNeeded() {
      this.isAddingGift = true;
      this.hideMessage();

      try {
        const cart = await this.fetchCart();

        const triggerProductInCart = cart.items.some((item) => {
          return (
            Number(item.product_id) ===
            this.triggerProductId
          );
        });

        if (!triggerProductInCart) return;

        const giftAlreadyInCart = cart.items.some((item) => {
          return (
            Number(item.product_id) ===
            this.giftProductId
          );
        });

        if (giftAlreadyInCart) return;

        await this.addGift();
      } catch (error) {
        console.error(
          'Unable to add the promotional gift:',
          error
        );

        this.showMessage(
          error.message ||
          'The main product was added, but the free gift could not be added.'
        );
      } finally {
        this.isAddingGift = false;
      }
    }

    async fetchCart() {
      const response = await fetch(this.cartUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to check the cart.');
      }

      return response.json();
    }

    getSectionsToRender() {
      const sections = [];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections }
        })
      );

      return [...new Set(sections)];
    }

    async addGift() {
      const sections = this.getSectionsToRender();

      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: this.giftVariantId,
              quantity: 1,
              properties: {
                _promotional_gift_for:
                  String(this.triggerProductId)
              }
            }
          ],
          sections,
          sections_url: window.location.pathname
        })
      });

      const addedState = await response.json();

      if (!response.ok || addedState.status) {
        throw new Error(
          addedState.description ||
          addedState.message ||
          'The free gift is currently unavailable.'
        );
      }

      const cart = await this.fetchCart();
      cart.sections = addedState.sections || {};

      if (
        window.theme &&
        theme.pubsub &&
        theme.pubsub.PUB_SUB_EVENTS
      ) {
        theme.pubsub.publish(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          { cart }
        );
      }

      document.dispatchEvent(
        new CustomEvent('buy-get-gift:added', {
          detail: {
            cart,
            giftProductId: this.giftProductId,
            giftVariantId: this.giftVariantId
          }
        })
      );
    }

    showMessage(message) {
      if (!this.messageElement) return;

      this.messageElement.textContent = message;
      this.messageElement.hidden = false;
    }

    hideMessage() {
      if (!this.messageElement) return;

      this.messageElement.hidden = true;
      this.messageElement.textContent = '';
    }
  }

  customElements.define(
    'product-buy-get-gift',
    ProductBuyGetGift
  );
})();